import { useEffect, useMemo, useState } from 'react';
import { eventsApi } from '../../api/admin.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function EventsCalendar() {
  const { can } = useAuth();
  const canCreate = can('event', 'create');
  const [cursor, setCursor] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59), [cursor]);

  function refresh() {
    eventsApi.calendar(monthStart.toISOString(), monthEnd.toISOString()).then((d) => setItems(d?.items || []));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refresh, [monthStart.getTime()]);

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await eventsApi.create({
      title: fd.get('title'),
      description: fd.get('description') || null,
      type: fd.get('type'),
      startAt: fd.get('startAt'),
      endAt: fd.get('endAt'),
      location: fd.get('location') || null,
      meetingUrl: fd.get('meetingUrl') || null,
      budget: Number(fd.get('budget') || 0),
    });
    e.currentTarget.reset();
    setShowForm(false);
    refresh();
  }

  // Build a 6-week grid covering the visible month.
  const cells = useMemo(() => {
    const firstDow = monthStart.getDay();
    const start = new Date(monthStart); start.setDate(start.getDate() - firstDow);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [monthStart]);

  function eventsOn(d) {
    const ymd = d.toISOString().slice(0, 10);
    return items.filter((e) => {
      const s = e.start.slice(0, 10);
      const en = e.end.slice(0, 10);
      return ymd >= s && ymd <= en;
    });
  }

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>Events Calendar</h1>
          <div className="cal-nav">
            <button className="btn ghost small" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</button>
            <strong>{monthStart.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</strong>
            <button className="btn ghost small" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</button>
            <button className="btn ghost small" onClick={() => setCursor(new Date())}>Today</button>
          </div>
        </div>
        {canCreate && <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Event'}</button>}
      </header>

      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label className="full">Title<input name="title" required /></label>
          <label>Type
            <select name="type" defaultValue="internal">
              <option>internal</option><option>project</option><option>external</option>
              <option>training</option><option>meeting</option>
            </select>
          </label>
          <label>Budget<input name="budget" type="number" step="0.01" defaultValue={0} /></label>
          <label>Start<input name="startAt" type="datetime-local" required /></label>
          <label>End<input name="endAt" type="datetime-local" required /></label>
          <label>Location<input name="location" /></label>
          <label>Meeting URL<input name="meetingUrl" /></label>
          <label className="full">Description<textarea name="description" rows={2} /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}

      <div className="card">
        <div className="cal-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="cal-head muted small">{d}</div>)}
          {cells.map((d) => {
            const inMonth = d.getMonth() === monthStart.getMonth();
            const evts = eventsOn(d);
            return (
              <div key={d.toISOString()} className={`cal-cell ${inMonth ? '' : 'muted'}`} style={{ aspectRatio: 'auto', minHeight: 70 }}>
                <span className="day">{d.getDate()}</span>
                {evts.slice(0, 3).map((e) => (
                  <button key={e.id} className={`event-pill type-${e.type}`} onClick={() => setSelected(e)}>{e.title}</button>
                ))}
                {evts.length > 3 && <span className="muted xs">+{evts.length - 3} more</span>}
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="card">
          <header className="page-header">
            <h3>{selected.title}</h3>
            <button className="btn ghost small" onClick={() => setSelected(null)}>✕</button>
          </header>
          <p className="muted small">{new Date(selected.start).toLocaleString()} → {new Date(selected.end).toLocaleString()}</p>
          {selected.location && <p>📍 {selected.location}</p>}
          <p><span className="chip">{selected.type}</span> · organized by {selected.organizer || '—'}</p>
        </div>
      )}
    </section>
  );
}
